/**
 * @fileoverview Loading skeleton component for analysis wizard
 */

import React from 'react';
import TrebbleSpace from '../../common/TrebbleSpace.tsx';
import { TrebbleTitle } from '../../common/TrebbleTypography.tsx';
import TrebbleSkeleton from '../../common/TrebbleSkeleton.tsx';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';

const { Title } = { Title: TrebbleTitle };

/**
 * Loading skeleton component displayed while analysis is in progress
 * @param {Object} props - Component props
 * @param {boolean} [props.showLoadingMessage=true] - Whether to show loading text and decorative blocks
 * @param {boolean} [props.useEditorTheme] - Whether to use editor theme for components
 * @returns {React.ReactElement}
 */
export default function AnalysisLoadingSkeleton({ showLoadingMessage = true, useEditorTheme }) {
  // Show only the initial analysis message since this is specifically for initial loading
  const loadingMessage = window.getI18n(ti18n, 'INITIAL_ANALYSIS_LOADING');

  return (
    <TrebbleSpace direction="vertical" className="w-full" useEditorTheme={useEditorTheme}>
      {/* Title skeleton */}
      <div className="mb-6">
        <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '75%' }} title={false} />
      </div>

      {/* Question 1 - Content type */}
      <div className="mb-5">
        <div className="mb-3">
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '52%' }} title={false} />
        </div>
        <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '80%' }} title={false} />
      </div>

      {/* Question 2 - Platform */}
      <div className="mb-5">
        <div className="mb-3">
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '48%' }} title={false} />
        </div>
        <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '80%' }} title={false} />
      </div>

      {/* Loading message centered within skeleton content - no border */}
      {showLoadingMessage && (
        <div className="my-5 text-center">
          {/* Complete loading section with decorative skeleton blocks */}
          <div className="flex items-center justify-start max-w-full">
            {/* Left shimmer block */}
            <div className="mr-5 w-[180px] shrink-0">
              <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 4, width: ['85%', '100%', '70%', '90%'] }} title={false} />
            </div>
            
            {/* Central loading text block */}
            <div className="text-center max-w-[320px] flex-1">
              {/* Main loading message */}
              <div className="text-lg font-bold leading-relaxed mb-4 break-words animate-shimmer-text">
                {loadingMessage}
              </div>
              
              {/* Time expectation text */}
              <div className="text-sm leading-relaxed opacity-80 break-words animate-shimmer-text">
                {window.getI18n(ti18n, 'INITIAL_ANALYSIS_TIME_EXPECTATION')}
              </div>
            </div>
            
            {/* Right shimmer block */}
            <div className="ml-5 w-[180px] shrink-0">
              <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 4, width: ['75%', '90%', '65%', '100%'] }} title={false} />
            </div>
          </div>
        </div>
      )}

      {/* Question 3 - Goal */}
      <div className="mb-5">
        <div className="mb-3">
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '58%' }} title={false} />
        </div>
        <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '80%' }} title={false} />
      </div>

      {/* Question 4 - Directives */}
      <div className="mb-5">
        <div className="mb-3">
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '68%' }} title={false} />
        </div>
        <div className="w-full">
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '90%' }} className="mb-3" title={false} />
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '90%' }} className="mb-3" title={false} />
          <TrebbleSkeleton useEditorTheme={useEditorTheme} active paragraph={{ rows: 1, width: '65%' }} className="mb-3" title={false} />
        </div>
      </div>
    </TrebbleSpace>
  );
} 